/** @module api/groups */

import { apiCall } from '@/api/index'
import { addQueryParamsToReqPayload } from '@/api/configure'
import {
  apiV1GroupsPath,
  apiV1GroupPath,
  hierarchyApiV1GroupPath,
  relationshipEmssApiV1GroupPath,
  accessorsApiV1GroupPath,
  claimUserApiV1GroupPath,
  claimEmsApiV1GroupPath
} from '@/../lib/routes'

/**
 * API GET to get a collection of (Organization) Groups
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.fields (optional) is a list of resource fields (attributes) to be included in the returned collection
 * @param {object} params.filter (optional) is a filter to by applied to the resource query
 * @param {string} params.sort (optional) is a sort-string (resource attribute/field name) the returned collection should be sorted by.
 *
 * @return {promise}
 */
export function getGroups({
  ctxt = 'admin',
  fields = [],
  filter = {},
  sort = '',
  type = 'Groups::OrganizationGroup',
  parentId = undefined
} = {}) {
  const payload = {}
  parentId = parentId === null ? 'NULL' : parentId
  if (parentId) {
    Object.assign(filter, { parentId })
  }
  if (type) {
    Object.assign(filter, { type })
  }
  addQueryParamsToReqPayload(payload, 'groups', { fields, filter, sort })

  return apiCall({
    path: apiV1GroupsPath(ctxt),
    payload
  })
}

/**
 * API GET to get a Group by ID
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the group's ID
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.fields (optional) is a list of resource fields (attributes) to be included in the returned collection
 * @param {array} params.include (optional) is a list of relationships to be included, e.g. `['energy_management_systems']` for a OrganizationGroup or `['energy_management_system']` for a EmsAccessGroup
 *
 * @return {promise} with resolves the the returned group
 */
export function getGroup({ id, ctxt = 'admin', fields = [], include = [] } = {}) {
  const payload = {}
  addQueryParamsToReqPayload(payload, null, { fields, include })

  return apiCall({
    path: apiV1GroupPath(ctxt, id),
    payload
  })
}

/**
 * API GET to get a tree for a Group by ID
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the group's ID
 * @param {string} params.ctxt is the context of the request
 *
 * @return {promise} with resolves the the returned group
 */
export function getHierarchy({ id, ctxt = 'admin' } = {}) {
  return apiCall({
    path: hierarchyApiV1GroupPath(ctxt, id)
  })
}

/**
 * API GET to the EMS ids for a given group ID
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the group's ID
 * @param {string} params.ctxt is the context of the request
 * @param {Boolean} params.includeDescendants, any not falsy value will trigger the call to include descendants
 *
 * @return {promise} with resolves for the returned group
 */
export function getEmsIdsForGroup({ id, ctxt = 'admin', includeDescendants = false } = {}) {
  let payload = undefined

  if (includeDescendants) {
    payload = {
      includeDescendants: 'yes'
    }
  }

  return apiCall({
    path: relationshipEmssApiV1GroupPath(ctxt, id),
    payload
  })
}

/**
 * API GET to the accessing Users for a given group ID
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the group's ID
 * @param {string} params.ctxt is the context of the request
 *
 * @return {promise} with resolves for the returned group
 */
export function getUsersForGroup({ id, ctxt = 'admin' } = {}) {
  return apiCall({
    path: accessorsApiV1GroupPath(ctxt, id)
  })
}

/**
 * API PATCH to the change the name for a group ID
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the group's ID
 * @param {string} params.ctxt is the context of the request
 *
 * @return {promise} with resolves for the returned group
 */
export function updateGroup({ id, ctxt = 'admin', name = undefined, tag = undefined } = {}) {
  const payload = {
    data: {
      type: 'groups',
      id,
      attributes: {
        name,
        tag
      }
    }
  }

  return apiCall({
    method: 'PATCH',
    path: apiV1GroupPath(ctxt, id),
    payload
  })
}

/**
 * API POST to the create a new group under the parent ID
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the group's ID
 * @param {string} params.ctxt is the context of the request
 *
 * @return {promise} with resolves for the returned group
 */
export function createGroup({ id, ctxt = 'admin', name = undefined, tag = undefined, type = undefined } = {}) {
  const payload = {
    data: {
      type: 'groups',
      attributes: {
        name,
        parent_id: id,
        tag,
        type
      }
    }
  }

  return apiCall({
    method: 'POST',
    path: apiV1GroupsPath(ctxt),
    payload
  })
}

/**
 * API DELETE to the destroy a Group with the given Id
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the group's ID
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.memberType safe memberType 'users' | 'energyManagementSystems'
 *
 * @return {promise} with resolves for the returned group
 */
export function deleteGroup({ id, ctxt = 'admin', memberType = undefined } = {}) {
  const payload = {
    meta: {
      safe: true,
      memberType
    }
  }

  return apiCall({
    method: 'DELETE',
    path: apiV1GroupPath(ctxt, id),
    payload
  })
}

/**
 * API POST to the claim an user to a group via email
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the group's ID
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.email has to be a user's email
 *
 * @return {promise} with resolves for the returned group
 */
export function claimUserToGroup({ id, ctxt = 'admin', email = '' } = {}) {
  const payload = {
    data: {
      type: 'users',
      attributes: {
        email
      }
    }
  }

  return apiCall({
    method: 'POST',
    path: claimUserApiV1GroupPath(ctxt, id),
    payload
  })
}

/**
 * API POST to the claim an ems to a group via serialno
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the group's ID
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.serialno has to be a valid serialno
 *
 * @return {promise} with resolves for the returned group
 */
export function claimEmsToGroup({ id, ctxt = 'admin', serialno = '' } = {}) {
  const payload = {
    data: {
      type: 'energy_management_systems',
      relationships: {
        amperix: {
          data: {
            type: 'amperix',
            id: serialno
          }
        }
      }
    }
  }

  return apiCall({
    method: 'POST',
    path: claimEmsApiV1GroupPath(ctxt, id),
    payload
  })
}
